/**
 * Transport Mode Types - Phase 4 LTL/TL Integration
 *
 * These types define the transport mode for orders and shipments.
 * Transport mode is stored on Shipment and exposed via Order properties.
 */

export type TransportMode = 'parcel' | 'ltl' | 'tl' | 'will_call'

export const TransportModeLabels: Record<TransportMode, string> = {
  parcel: 'Parcel',
  ltl: 'LTL',
  tl: 'TL',
  will_call: 'Will Call',
}

export const TransportModeOptions: { value: TransportMode; label: string }[] = [
  { value: 'parcel', label: 'Parcel' },
  { value: 'ltl', label: 'LTL (Less Than Truckload)' },
  { value: 'tl', label: 'TL (Full Truckload)' },
  { value: 'will_call', label: 'Will Call (Customer Pickup)' },
]

/**
 * Check if a transport mode is non-parcel (LTL or TL)
 */
export const isNonParcelMode = (mode: TransportMode | undefined | null): boolean => {
  return mode === 'ltl' || mode === 'tl'
}

/**
 * Check if a transport mode is will call (customer pickup)
 */
export const isWillCall = (mode: TransportMode | undefined | null): boolean => {
  return mode === 'will_call'
}

/**
 * Check if a transport mode does not require parcel rate shopping
 * (LTL, TL, and will call all skip label purchase / rate shopping)
 */
export const isNonParcel = (mode: TransportMode | undefined | null): boolean => {
  return isNonParcelMode(mode) || isWillCall(mode)
}

/**
 * Filter options for transport mode in order list (string-typed values for react-select compat)
 */
export const TransportModeFilterOptions: { value: string; label: string }[] = TransportModeOptions.map((o) => ({
  value: o.value as string,
  label: o.label,
}))

/**
 * Billing methods for shipments
 */
export type ShipmentBillingMethod = 'prepaid' | 'collect' | 'third_party'

export const ShipmentBillingMethodLabels: Record<ShipmentBillingMethod, string> = {
  prepaid: 'Prepaid',
  collect: 'Collect',
  third_party: 'Third Party',
}

/**
 * Contracting party for LTL/TL shipments (who arranges transport)
 */
export type ContractingParty = 'shipper' | 'merchant' | 'consignee'

export const ContractingPartyOptions: { value: ContractingParty; label: string }[] = [
  { value: 'shipper', label: 'Shipper (Warehouse)' },
  { value: 'merchant', label: 'Merchant (Brand)' },
  { value: 'consignee', label: 'Consignee (Receiver)' },
]

/**
 * Shipping account detail (matches BE ShippingAccountSerializer)
 */
export interface ShippingAccountDetail {
  id: number
  company: number
  carrier: number
  carrier_name: string
  billing_account: string
  billing_zip: string
  billing_country: string
  created: string
  updated: string
}

/**
 * Shipment type - represents the shipping details for an order
 */
export interface Shipment {
  id: number
  shipment_id: string
  order_id: number
  transport_mode: TransportMode
  bill_of_lading_number: string | null
  pro_number: string | null
  billing_method: ShipmentBillingMethod | null
  billing_account: number | null
  billing_account_detail: ShippingAccountDetail | null
  carrier_id: number | null
  carrier_name: string | null
  contracting_party: ContractingParty | null
  is_freight: boolean
  is_parcel: boolean
  created: string
  updated: string
}
